import React from "react";
import { Card, Progress, Popover, Icon } from "antd";
import { MentionedDomainMetrics } from "../types";
import { getVisibilityScoreColor, getSovColor } from "./utils";

const { Meta } = Card;
type Props = {
    firstMentionedDomain: MentionedDomainMetrics | null;
    lastMentionedDomain: MentionedDomainMetrics | null;
    targetDomain: MentionedDomainMetrics;
}

export default function VisibilityAnalysisScores({ targetDomain, firstMentionedDomain, lastMentionedDomain }: Props) {
    return (
        <Card
            type="inner"
            className="visibility-scores-card"
            title={
                <Meta title={"Your LLM Visibility Score"} description={"Based on LLM mentions and Share of Voice"} />
            }
        >
            <div className="visibility-scores-container">
                <div className="visibility-score">
                    <div className="title">
                        <h4>Visibility Score</h4>
                        <Popover
                            className="visibility-score-popover"
                            placement="leftTop"
                            overlayStyle={{ maxWidth: '360px' }}
                            content={
                                <div>
                                    The <strong>Visibility Score</strong> is the normalized value (0-100) of the percentage of total mentions your domain receives from LLMs compared to all other domains in your industry niche.
                                    A higher score indicates that your domain is frequently recognized and recommended by LLMs, enhancing its visibility in AI-driven search results.
                                </div>}
                        >
                            <Icon className="tooltip" type="question-circle" theme="filled" />
                        </Popover>
                    </div>
                    <Progress
                        type="circle"
                        percent={targetDomain.visibility_score}
                        strokeWidth={6}
                        width={100}
                        strokeColor={getVisibilityScoreColor(targetDomain.visibility_score)}
                        format={(percent) => `${percent}/100`}
                    />

                    {firstMentionedDomain && lastMentionedDomain && (
                        <div className="competitor-range">
                            <span className="label">Competitor range:</span>
                            <span className="range">{lastMentionedDomain.visibility_score} - {firstMentionedDomain.visibility_score}</span>
                        </div>
                    )}
                </div>

                <div className="visibility-score">
                    <div className="title">
                        <h4>Share of Voice</h4>
                        <Popover
                            className="visibility-score-popover"
                            placement="leftTop"
                            overlayStyle={{ maxWidth: '360px' }}
                            content={
                                <div>
                                    <strong>Share of Voice (SOV)</strong> indicates the percentage of total mentions your domain receives from LLMs compared to all other domains in your industry niche.
                                    A higher SOV means your domain is more prominently featured in AI-generated responses, reflecting its influence and presence in the market.
                                </div>
                            }
                        >
                            <Icon className="tooltip" type="question-circle" theme="filled" />
                        </Popover>
                    </div>
                    <Progress
                        type="circle"
                        percent={targetDomain.share_of_voice}
                        strokeWidth={6}
                        width={100}
                        strokeColor={getSovColor(targetDomain.share_of_voice, firstMentionedDomain ? firstMentionedDomain.share_of_voice : targetDomain.share_of_voice)}
                        format={(percent) => `${percent}%`}
                    />

                    {firstMentionedDomain && lastMentionedDomain && (
                        <div className="competitor-range">
                            <span className="label">Competitor range:</span>
                            <span className="range">{lastMentionedDomain.share_of_voice.toFixed(1)}% - {firstMentionedDomain.share_of_voice.toFixed(1)}%</span>
                        </div>
                    )}
                </div>
            </div>
        </Card>
    );
}